import { clone, mergeRight } from "ramda";
import Controller from "@/components/Media/Controller.vue";
import NumberBox from "@/components/forms/inputs/NumberBox.vue";
import Password from "@/components/forms/inputs/Password.vue";
import Phone from "@/components/forms/inputs/Phone.vue";
import Previewer from "@/components/Media/Previewer.vue";
import RadioGroup from "@/components/forms/inputs/RadioGroup.vue";
import TextArea from "@/components/forms/inputs/TextArea.vue";
import TextBox from "@/components/forms/inputs/Text.vue";

// search helper
import { mapGetters } from "vuex";

export const roleManager = {
  computed: {
    ...mapGetters({ authUser: "auth/auth" }),
  },

  created() {
    this.isSuperadmin = this.authUser.role === "superadmin";
    this.isStaff = this.authUser.role === "staff";
    this.isCustomer = this.authUser.role === "customer";
  },
};

export const progressWrapper = {
  data() {
    return {
      isLoading: false,
    };
  },

  methods: {
    async wrapProgress(fn) {
      this.isLoading = true;
      await fn();
      this.isLoading = false;
    },
  },
};

export const formHelper = {
  components: {
    NumberBox,
    Password,
    Phone,
    RadioGroup,
    TextArea,
    TextBox,
  },

  mixins: [progressWrapper, roleManager],

  props: {
    model: {
      type: Object,
      default: () => ({}),
    },

    validate: {
      type: Function,
      required: true,
    },

    valid: {
      type: Boolean,
      required: true,
    },

    reset: {
      type: Function,
      default: null,
      required: false,
    },
  },

  data() {
    return {
      errors: {},
    };
  },

  computed: {
    isEdit() {
      return this.$route.name.includes("edit");
    },

    hasError() {
      return Object.keys(this.errors).length > 0;
    },
  },

  methods: {
    clearError(key) {
      this.$delete(this.errors, key);
    },

    flushError() {
      this.errors = {};
    },

    displayError(error) {
      this.errors = mergeRight(this.errors, error);
    },

    setError(error, route = null) {
      this.errors = error.response.data.errors || {};
      if (route) {
        this.$router.push({ name: route });
      }
    },
  },
};

export const exportCSV = {
  methods: {
    exportCSV({ type, fileName, params }) {
      window.$eventHub.$emit("toggleLoader", { state: true });
      this.$store.dispatch(`${type}/exportData`, params).then((response) => {
        window.$eventHub.$emit("toggleLoader", { state: false });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
      });
    },
  },
};

export const tableHelper = {
  mixins: [exportCSV],
  methods: {
    fetch({ skip, limit }) {
      const { type, params } = this.prepareFetchPayload();

      this.$store.commit(`${type}/dynamicSet`, {
        pagination: {
          skip: skip || 0,
          limit: limit || 10,
        },
      });

      return this.wrapProgress(() => {
        this.$store.dispatch(
          `${type}/fetch`,
          mergeRight(params, {
            sortBy: this.misc.sortBy,
            sortDesc: this.misc.sortDesc,
          })
        );
      }).catch((error) => {
        this.$router.push({ name: "dashboard" });
      });
    },
  },
};

export const imageViewer = {
  components: {
    Controller,
    Previewer,
  },

  data: () => ({
    previewer: false,
    selections: [],
  }),

  methods: {
    previewMedia(media) {
      if (this.exist(media.id)) {
        this.selections = this.selections.filter((x) => x.id !== media.id);
      } else if (this.selections.length === 4) {
        this.selections.push(clone(media));
        this.selections.shift();
      } else {
        this.selections.push(clone(media));
      }
    },

    flushSelections() {
      this.selections = [];
    },

    exist(id) {
      return this.selections.find((x) => x.id === id) !== undefined;
    },

    download({ id }) {
      this.$store.dispatch("media/download", id);
    },

    remove(id) {
      this.wrapProgress(() =>
        this.$store.dispatch("media/remove", id).then(() => {
          this.selections = [];
          this.fetchMedia();
        })
      );
    },

    showLogs(log) {
      window.$eventHub.$emit("showPopUpLog", {
        date: log.data,
        type: "media",
        type_id: log.id,
        description: log.name,
      });
    },
  },
};
